import { TeamsSelection } from '@/components/ui/visbility-settings/teams-selection';
import {
  computeWorkflowShareDiff,
  isSharedWithWorkspace,
  sharedTeamSet,
} from '@heidi/agents/contracts';
import { useWorkspaceShareTarget } from '@heidi/agents/data';
import { Button } from '@heidi/design-system/button';
import { Checkbox } from '@heidi/design-system/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@heidi/design-system/dialog';
import { Label } from '@heidi/design-system/label';
import { toast } from '@heidi/design-system/toast';
import { TypographyCaption } from '@heidi/design-system/typography';
import { useOrganizationInfo, useWorkspaceId } from '@heidi/workspace/data';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useShareWorkflow, useUnshareWorkflow } from '../hooks';
import type { Workflow } from '../types';

interface ShareWorkflowDialogProps {
  workflow: Workflow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Share a workflow with the current Heidi workspace, specific org teams, or
 * (org admin) the whole organization. Maps to POST share/unshare diffs.
 */
export function ShareWorkflowDialog({
  workflow,
  open,
  onOpenChange,
}: ShareWorkflowDialogProps): JSX.Element {
  const intl = useIntl();
  const workspaceId = useWorkspaceId();
  const { hasOrganization, isGlobalAdmin, orgTeams } = useOrganizationInfo();
  const {
    data: workspaceShareTarget,
    isPending: isWorkspaceShareTargetPending,
    isError: isWorkspaceShareTargetError,
  } = useWorkspaceShareTarget(workspaceId);
  const share = useShareWorkflow(workflow.workflow_id);
  const unshare = useUnshareWorkflow(workflow.workflow_id);

  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [shareOrg, setShareOrg] = useState(false);
  const [shareCurrentWorkspace, setShareCurrentWorkspace] = useState(false);
  const [teamsOpen, setTeamsOpen] = useState(false);

  const workspaceShareScope = workspaceShareTarget?.scope;
  const workspaceShareTargetId = workspaceShareTarget?.target_id;

  useEffect(() => {
    if (!open) return;
    const current = sharedTeamSet(workflow);
    setSelectedTeamIds([...current]);
    setShareOrg(workflow.visibility === 'Organization');
    setShareCurrentWorkspace(
      isSharedWithWorkspace(workflow, {
        scope: workspaceShareScope ?? 'unsupported',
        target_id: workspaceShareTargetId,
      })
    );
  }, [open, workflow, workspaceShareScope, workspaceShareTargetId]);

  const canPickTeams = hasOrganization && (orgTeams?.length ?? 0) > 0;
  const canShareOrg = Boolean(isGlobalAdmin);
  const canShareWorkspace = Boolean(workspaceId && workspaceShareTarget?.can_share);
  const isUnsupportedWorkspace =
    workspaceShareTarget?.reason === 'personal_or_unknown_workspace';
  const isOrganizationWorkspaceShareRestricted =
    workspaceShareTarget?.reason === 'organization_admin_required';
  const isTeamWorkspaceShareRestricted =
    workspaceShareTarget?.reason === 'team_manager_required';
  const isLoadingWorkspaceShareTarget = Boolean(
    workspaceId && isWorkspaceShareTargetPending
  );
  const isSaving = share.isPending || unshare.isPending;

  const initialTeamIds = useMemo(() => [...sharedTeamSet(workflow)], [workflow]);
  const wasOrg = workflow.visibility === 'Organization';
  const wasWorkspaceShared = isSharedWithWorkspace(workflow, workspaceShareTarget);

  const handleSave = useCallback(async () => {
    const diff = computeWorkflowShareDiff({
      workspaceId,
      workspaceTargetId: workspaceShareTargetId,
      selectedTeamIds,
      shareCurrentWorkspace,
      shareOrg,
      wasOrg,
      wasWorkspaceShared,
      initialTeamIds,
    });

    try {
      if (diff.shareOrg) {
        await share.mutateAsync({ organization: true });
      }
      if (diff.unshareOrg) {
        await unshare.mutateAsync({ organization: true });
      }
      if (diff.workspaceAdd && workspaceId) {
        await share.mutateAsync({ workspace_id: workspaceId });
      }
      if (diff.teamAdds.length > 0) {
        await share.mutateAsync({ team_ids: diff.teamAdds });
      }
      if (diff.workspaceRemove && workspaceId) {
        await unshare.mutateAsync({ workspace_id: workspaceId });
      }
      if (diff.teamRemoves.length > 0) {
        await unshare.mutateAsync({ team_ids: diff.teamRemoves });
      }

      toast({
        title: intl.formatMessage({
          defaultMessage: 'Sharing updated',
          id: '4yUW4cW5Tq',
        }),
        tone: 'success',
      });
      onOpenChange(false);
    } catch {
      // Toasts live on the mutation hooks.
    }
  }, [
    selectedTeamIds,
    shareCurrentWorkspace,
    workspaceId,
    workspaceShareTargetId,
    initialTeamIds,
    shareOrg,
    wasOrg,
    wasWorkspaceShared,
    share,
    unshare,
    onOpenChange,
    intl,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage defaultMessage="Share routine" id="j9wErOsOAI" />
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 pt-1">
          <TypographyCaption className="text-text-secondary">
            <FormattedMessage
              defaultMessage="Teammates you share with can list and run “{name}”. Only you can edit or schedule it."
              id="hykg/qfc37"
              values={{ name: workflow.name }}
            />
          </TypographyCaption>

          {canShareWorkspace && workspaceId ? (
            <label className="flex cursor-pointer items-start gap-3">
              <Checkbox
                checked={shareCurrentWorkspace}
                onCheckedChange={(v) => setShareCurrentWorkspace(v === true)}
                className="mt-0.5"
              />
              <span className="flex min-w-0 flex-col gap-0.5">
                <Label className="cursor-pointer font-medium text-text-primary">
                  <FormattedMessage defaultMessage="Current workspace" id="fvJJOAby+S" />
                </Label>
                <TypographyCaption className="text-text-tertiary">
                  <FormattedMessage
                    defaultMessage="Share with everyone in this Heidi workspace"
                    id="ZWAV4eUCdl"
                  />
                </TypographyCaption>
              </span>
            </label>
          ) : null}

          {canPickTeams ? (
            <div className="flex flex-col gap-1.5">
              <Label>
                <FormattedMessage defaultMessage="Teams" id="DNeuzb5ky2" />
              </Label>
              <TeamsSelection
                selectedValues={selectedTeamIds}
                setSelectedValues={setSelectedTeamIds}
                open={teamsOpen}
                setOpen={setTeamsOpen}
                creatorTeam={workflow.team_id ?? undefined}
              />
            </div>
          ) : null}

          {canShareOrg ? (
            <label className="flex cursor-pointer items-start gap-3">
              <Checkbox
                checked={shareOrg}
                onCheckedChange={(v) => setShareOrg(v === true)}
                className="mt-0.5"
              />
              <span className="flex min-w-0 flex-col gap-0.5">
                <Label className="cursor-pointer font-medium text-text-primary">
                  <FormattedMessage
                    defaultMessage="Entire organization"
                    id="cX2GOWO56t"
                  />
                </Label>
                <TypographyCaption className="text-text-tertiary">
                  <FormattedMessage
                    defaultMessage="Everyone in your organization can run this routine"
                    id="ne8IKKYeA2"
                  />
                </TypographyCaption>
              </span>
            </label>
          ) : null}

          {isWorkspaceShareTargetError ? (
            <TypographyCaption className="text-text-secondary">
              <FormattedMessage
                defaultMessage="Workspace sharing is unavailable right now. Try again shortly."
                id="7sJOPdprX5"
              />
            </TypographyCaption>
          ) : isUnsupportedWorkspace ? (
            <TypographyCaption className="text-text-secondary">
              <FormattedMessage
                defaultMessage="Switch to a team or organization workspace to share this routine."
                id="nOStLQY9qJ"
              />
            </TypographyCaption>
          ) : isOrganizationWorkspaceShareRestricted && !canPickTeams ? (
            <TypographyCaption className="text-text-secondary">
              <FormattedMessage
                defaultMessage="Only an organization admin can share a routine with the whole workspace."
                id="o1HArvUEp7"
              />
            </TypographyCaption>
          ) : isTeamWorkspaceShareRestricted ? (
            <TypographyCaption className="text-text-secondary">
              <FormattedMessage
                defaultMessage="Only account owners, administrators, and template managers can share routines with a team."
                id="VoSykecH8F"
              />
            </TypographyCaption>
          ) : !isLoadingWorkspaceShareTarget &&
            !canShareWorkspace &&
            !canPickTeams &&
            !canShareOrg ? (
            <TypographyCaption className="text-text-secondary">
              <FormattedMessage
                defaultMessage="Join a team or organization workspace to share routines."
                id="x32pstYmf1"
              />
            </TypographyCaption>
          ) : null}
        </div>

        <DialogFooter className="gap-2">
          <Button
            size="lg"
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            <FormattedMessage defaultMessage="Cancel" id="47FYwba+bI" />
          </Button>
          <Button
            size="lg"
            type="button"
            onClick={() => void handleSave()}
            disabled={isSaving || (!canShareWorkspace && !canPickTeams && !canShareOrg)}
          >
            {isSaving
              ? intl.formatMessage({ defaultMessage: 'Saving…', id: 'WUV28A6Pf2' })
              : intl.formatMessage({ defaultMessage: 'Save', id: 'jvo0vs3nF0' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
